/**
 * Colours for blocks when there are no game textures to take them from ("Map colours only": a guest without a
 * Minecraft jar). Guessed from the block's name — the first rule that matches wins, so specific words come first.
 * Nothing here comes from Mojang's files; it only has to look right at a glance in the Schematics preview.
 */

const DYES: Record<string, number> = {
  white: 0xe9ecec, light_gray: 0x8e8e86, gray: 0x3e4447, black: 0x1d1d21, brown: 0x724728, red: 0xa02722,
  orange: 0xf07613, yellow: 0xf8c527, lime: 0x70b919, green: 0x546d1b, cyan: 0x158991, light_blue: 0x3aafd9,
  blue: 0x35399d, purple: 0x792aac, magenta: 0xbd44b3, pink: 0xed8dac,
};

/** wood types: planks colour, log bark colour */
const WOODS: [string, number, number][] = [
  ["pale_oak", 0xe0d6d0, 0x5c5450], ["dark_oak", 0x42291a, 0x3c2e1c], ["oak", 0xa2824e, 0x6d5532], ["spruce", 0x725432, 0x3a2510],
  ["birch", 0xc0af79, 0xd8d7d2], ["jungle", 0xa07350, 0x554419], ["acacia", 0xa85a32, 0x676157], ["mangrove", 0x763731, 0x544232],
  ["cherry", 0xe2b2ac, 0x371f2a], ["bamboo", 0xc2af50, 0x8a8c2e], ["crimson", 0x653046, 0x5c1a1e], ["warped", 0x2b6963, 0x3a3a4d],
];

const RULES: [RegExp, number][] = [
  [/water|bubble_column/, 0x3f76e4], [/lava|magma/, 0xcf5c14], [/ice$|packed_ice|blue_ice/, 0x91b7f8], [/snow|powder_snow/, 0xf9fefe],
  [/glass/, 0xc0dbe6], [/leaves|azalea|vine|moss|grass_block|lily|kelp|seagrass|fern|^grass$|short_grass|tall_grass|bush|sapling/, 0x5f9f35],
  [/dirt_path/, 0xa4845a], [/farmland/, 0x6b4a2c], [/mycelium/, 0x6f6265], [/podzol/, 0x5b3f18], [/mud_brick/, 0x8a6b51], [/packed_mud/, 0x8e6b50], [/mud/, 0x3c3837],
  [/coarse_dirt|rooted_dirt|dirt/, 0x866043], [/red_sand/, 0xbe6621], [/soul_sand|soul_soil/, 0x51402f], [/sandstone|sand/, 0xdbcfa3], [/gravel/, 0x837f7e], [/clay/, 0xa0a6b3],
  [/deepslate/, 0x505052], [/blackstone|basalt/, 0x2a2328], [/tuff/, 0x6c6d66], [/calcite|diorite|quartz|bone_block/, 0xe0ddd5], [/andesite/, 0x888889],
  [/granite/, 0x956755], [/end_stone|end_brick/, 0xdbde9e], [/purpur/, 0xa97ea9], [/prismarine/, 0x5aa39a], [/nether_brick|nether_wart|netherrack/, 0x5a2a2c],
  [/stone_brick/, 0x7a797a], [/bricks?$|brick_/, 0x964a3a], [/terracotta/, 0x985e43], [/obsidian/, 0x14121e], [/copper/, 0xc06c50], [/gold/, 0xf6d03d], [/iron|anvil|chain|bars/, 0xc8c8c8],
  [/diamond/, 0x62dbd5], [/emerald/, 0x2ecc71], [/lapis/, 0x2551a6], [/redstone/, 0xa01a0e], [/amethyst/, 0x8a62c5], [/glowstone|lantern|torch|shroomlight|froglight/, 0xf0c060],
  [/hay/, 0xc6a51e], [/pumpkin|melon/, 0xc77819], [/bookshelf|lectern|chest|barrel|crafting|table|loom|composter/, 0x9a6c3c], [/sponge/, 0xc3c24a],
  [/cobble|stone|furnace|smoker|cauldron|dispenser|dropper|observer|piston|slab|wall$|button|pressure/, 0x7f7f7f],
  [/poppy|rose|tulip|dandelion|flower|orchid|allium|bluet|daisy|lilac|peony|cornflower/, 0xc8503c],
  [/mushroom/, 0xa0522d], [/wool|carpet|bed$/, 0xe9ecec], [/concrete/, 0xcfd5d6],
];

/** longest names first: "light_gray_wool" is light grey, not grey */
const DYE_ORDER = Object.entries(DYES).sort((a, b) => b[0].length - a[0].length);

const cache = new Map<string, number>();

/** "minecraft:spruce_stairs" → 0x725432 (a guess; grey for anything unknown) */
export function guessBlockColour(id: string): number {
  const hit = cache.get(id);
  if (hit !== undefined) return hit;
  const path = (id.includes(":") ? id.split(":", 2)[1] : id).replace(/^(waxed_|exposed_|weathered_|oxidized_|infested_|chiseled_|cracked_|polished_|smooth_|cut_|mossy_|stripped_)+/, "");
  let c: number | null = null;
  for (const [dye, v] of DYE_ORDER) if (path.startsWith(`${dye}_`)) { c = v; break; }
  if (c === null) for (const [w, planks, bark] of WOODS) if (path.includes(w)) { c = /log|wood|stem|hyphae/.test(path) ? bark : planks; break; }
  if (c === null) for (const [re, v] of RULES) if (re.test(path)) { c = v; break; }
  if (/weathered_|oxidized_/.test(id) && /copper/.test(id)) c = /oxidized/.test(id) ? 0x52a283 : 0x6c9d70;
  c ??= 0x8f979c;
  cache.set(id, c);
  return c;
}

/** 0xa2824e → [162, 130, 78] */
export const rgb = (c: number): [number, number, number] => [(c >> 16) & 255, (c >> 8) & 255, c & 255];
